import React, { useState, useEffect, useCallback } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { IoMdAdd } from 'react-icons/io';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { CiEdit } from 'react-icons/ci';
import { FaWhatsapp } from 'react-icons/fa';
import { MdOutlineArrowForwardIos } from 'react-icons/md';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi';
import { MdOutlinePublish } from 'react-icons/md';
import { MdOutlineArchive } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';

const API_BASE = `${process.env.REACT_APP_API_BASE}/auth/api` || '';

function decodeToken(token) {
    if (!token) return {};
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(
            decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            )
        );
    } catch {
        return {};
    }
}

function getUserIdFromToken() {
    const rawToken =
        typeof window !== 'undefined'
            ? (window.locatorgarsotrga?.('token') || localStorage.getItem('token'))
            : null;
    if (rawToken) {
        const decoded = decodeToken(rawToken);
        return { userId: decoded.id || decoded.userId || decoded._id, token: rawToken };
    }
    return { userId: null, token: null };
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
    const styles = {
        draft: 'bg-[#fff8e1] text-[#b45309] border border-[#fcd34d]',
        published: 'bg-[#e6f9f1] text-[#15803d] border border-[#6ee7b7]',
        archived: 'bg-[#f1f5f9] text-[#64748b] border border-[#cbd5e1]',
    };
    return (
        <span className={`text-[11px] font-bold px-2 py-[2px] rounded-full capitalize ${styles[status] || styles.draft}`}>
            {status}
        </span>
    );
}

// ── Dropdown menu ─────────────────────────────────────────────────────────────
function FlowMenu({ flow, onStatusChange, onDelete, onClose }) {
    useEffect(() => {
        const handler = () => onClose();
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [onClose]);

    const items = [
        flow.status !== 'published' && {
            label: 'Publish', icon: MdOutlinePublish, color: '#15803d',
            action: () => onStatusChange(flow.id, 'published')
        },
        flow.status !== 'archived' && {
            label: 'Archive', icon: MdOutlineArchive, color: '#64748b',
            action: () => onStatusChange(flow.id, 'archived')
        },
        flow.status !== 'draft' && {
            label: 'Revert to Draft', icon: HiOutlineDocumentDuplicate, color: '#b45309',
            action: () => onStatusChange(flow.id, 'draft')
        },
        {
            label: 'Delete', icon: RiDeleteBin6Fill, color: '#ed5050',
            action: () => onDelete(flow.id)
        }
    ].filter(Boolean);

    return (
        <div
            onClick={e => e.stopPropagation()}
            className='absolute right-0 top-8 z-50 bg-white border border-[#e3e9ef] rounded-xl shadow-xl py-1 w-44'
        >
            {items.map((item) => {
                const Icon = item.icon;
                return (
                    <button
                        key={item.label}
                        onClick={() => { item.action(); onClose(); }}
                        className='w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold hover:bg-[#f5f8fa] transition-colors'
                        style={{ color: item.color }}
                    >
                        <Icon size={14} /> {item.label}
                    </button>
                );
            })}
        </div>
    );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/30'>
            <div className='bg-white rounded-2xl shadow-2xl p-6 w-[320px]'>
                <p className='text-[14px] text-[#576071] font-semibold mb-5'>{message}</p>
                <div className='flex gap-2 justify-end'>
                    <button
                        onClick={onCancel}
                        className='px-4 py-2 rounded-lg border border-[#e3e9ef] text-[12px] text-[#576071] font-bold hover:bg-[#f5f8fa]'
                    >Cancel</button>
                    <button
                        onClick={onConfirm}
                        className='px-4 py-2 rounded-lg bg-[#ed5050] text-white text-[12px] font-bold hover:bg-[#c0392b]'
                    >Delete</button>
                </div>
            </div>
        </div>
    );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 3000);
        return () => clearTimeout(t);
    }, [onClose]);
    const colors = {
        success: 'bg-[#31b07b] text-white',
        error: 'bg-[#ed5050] text-white',
    };
    return (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-[13px] font-bold ${colors[type]}`}>
            {message}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function WAFlowIndex() {
    const navigate = useNavigate();
    const { token } = getUserIdFromToken();

    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [openMenuId, setOpenMenuId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
    }, []);

    // ── Fetch all flows ─────────────
    const fetchFlows = useCallback(() => {
        setLoading(true);
        fetch(`${API_BASE}/flows`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
            .then(r => r.json())
            .then(r => {
                if (r.ok && Array.isArray(r.data)) setFlows(r.data);
                else setFlows([]);
            })
            .catch(() => setFlows([]))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => { fetchFlows(); }, [fetchFlows]);

    // ── Status change ─────────────────
    const handleStatusChange = async (id, status) => {
        try {
            const r = await fetch(`${API_BASE}/flows/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ status })
            }).then(r => r.json());
            if (r.ok) {
                setFlows(prev => prev.map(f => f.id === id ? { ...f, status } : f));
                showToast(`Flow marked as ${status}`, 'success');
            } else {
                showToast('Status update failed', 'error');
            }
        } catch {
            showToast('Network error', 'error');
        }
    };

    // ── Delete ────────────────────────
    const handleDelete = async (id) => {
        try {
            const r = await fetch(`${API_BASE}/flows/${id}`, {
                method: 'DELETE',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            }).then(r => r.json());
            if (r.ok) {
                setFlows(prev => prev.filter(f => f.id !== id));
                showToast('Flow deleted', 'success');
            } else {
                showToast('Delete failed', 'error');
            }
        } catch {
            showToast('Network error', 'error');
        } finally {
            setConfirmDelete(null);
        }
    };

    // ── Filter + search ───────────────
    const filtered = flows.filter(f => {
        const matchesStatus = statusFilter === 'all' || f.status === statusFilter;
        const matchesSearch = search.trim() === '' ||
            (f.waba_id || '').toLowerCase().includes(search.toLowerCase()) ||
            String(f.id).includes(search);
        return matchesStatus && matchesSearch;
    });

    // ── Stats ─────────────────────────
    const stats = {
        total: flows.length,
        draft: flows.filter(f => f.status === 'draft').length,
        published: flows.filter(f => f.status === 'published').length,
        archived: flows.filter(f => f.status === 'archived').length,
    };

    return (
        <div className='dark:bg-[#121519] min-h-screen pt-[80px] px-6'>
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className='flex items-center justify-between mb-6'>
                <div>
                    <div className='flex items-center gap-2 text-[#61748f] text-[13px] mb-1'>
                        <FaWhatsapp className='text-[#37715d]' />
                        <span>WhatsApp</span>
                        <MdOutlineArrowForwardIos size={10} />
                        <span className='text-[#000] font-semibold'>Flows</span>
                    </div>
                    <h1 className='text-[22px] font-bold text-[#1a2535]'>WhatsApp Flows</h1>
                </div>
                <Link to='/WAMessage/WAFlow/AddEditFlow'>
                    <button className='flex items-center gap-2 bg-[#37715d] hover:bg-[#2d5f4e] text-white text-[13px] font-bold py-2 px-4 rounded-xl transition-colors shadow-sm'>
                        <IoMdAdd size={18} /> Create Flow
                    </button>
                </Link>
            </div>

            {/* ── Stat cards ──────────────────────────────────────────────── */}
            <div className='grid grid-cols-4 gap-3 mb-6'>
                {[
                    { label: 'Total', value: stats.total, color: '#37715d', bg: '#e6f9f1' },
                    { label: 'Draft', value: stats.draft, color: '#b45309', bg: '#fff8e1' },
                    { label: 'Published', value: stats.published, color: '#15803d', bg: '#dcfce7' },
                    { label: 'Archived', value: stats.archived, color: '#64748b', bg: '#f1f5f9' },
                ].map(card => (
                    <div
                        key={card.label}
                        onClick={() => setStatusFilter(card.label.toLowerCase() === 'total' ? 'all' : card.label.toLowerCase())}
                        className='rounded-xl p-4 cursor-pointer border border-[#e3e9ef] hover:shadow-md transition-shadow bg-white'
                    >
                        <p className='text-[12px] font-semibold text-[#61748f]'>{card.label}</p>
                        <p className='text-[26px] font-bold mt-1' style={{ color: card.color }}>{card.value}</p>
                    </div>
                ))}
            </div>

            {/* ── Search + filter bar ──────────────────────────────────────── */}
            <div className='flex items-center gap-3 mb-4'>
                <div className='flex items-center gap-2 border border-[#e3e9ef] rounded-xl px-3 py-2 bg-white flex-1 max-w-xs'>
                    <FiSearch className='text-[#61748f]' size={14} />
                    <input
                        type='text'
                        placeholder='Search by WABA ID or flow ID…'
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className='outline-none text-[12px] w-full bg-transparent text-[#1a2535] placeholder-[#aab4c4]'
                    />
                </div>
                <div className='flex gap-2'>
                    {['all', 'draft', 'published', 'archived'].map(s => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-2 rounded-xl text-[12px] font-semibold transition-colors capitalize border ${
                                statusFilter === s
                                    ? 'bg-[#37715d] text-white border-[#37715d]'
                                    : 'bg-white text-[#61748f] border-[#e3e9ef] hover:bg-[#f5f8fa]'
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Flow list ────────────────────────────────────────────────── */}
            {loading ? (
                <div className='flex items-center justify-center h-48'>
                    <AiOutlineLoading3Quarters className='animate-spin text-[#37715d] text-3xl' />
                </div>
            ) : filtered.length === 0 ? (
                <div className='flex flex-col items-center justify-center h-56 text-[#61748f]'>
                    <FaWhatsapp className='text-5xl text-[#37715d] mb-3 opacity-40' />
                    <p className='text-[14px] font-semibold'>
                        {flows.length === 0 ? 'No flows yet.' : 'No flows match your filter.'}
                    </p>
                    {flows.length === 0 && (
                        <Link to='/WAMessage/WAFlow/new'>
                            <button className='mt-4 flex items-center gap-1 bg-[#37715d] text-white text-[12px] font-bold px-4 py-2 rounded-xl hover:bg-[#2d5f4e]'>
                                <IoMdAdd /> Create your first flow
                            </button>
                        </Link>
                    )}
                </div>
            ) : (
                <div className='bg-white border border-[#e3e9ef] rounded-2xl  shadow-sm'>
                    {/* Table header */}
                    <div className='grid grid-cols-[60px_1fr_160px_140px_160px_100px_60px] px-4 py-3 bg-[#f5f8fa] border-b border-[#e3e9ef]'>
                        {['ID', 'WABA ID', 'Type', 'Status', 'Last Updated', 'Screens', ''].map(h => (
                            <span key={h} className='text-[11px] font-bold text-[#61748f] uppercase tracking-wide'>{h}</span>
                        ))}
                    </div>
                    {/* Rows */}
                    {filtered.map((flow, i) => (
                        <div
                            key={flow.id}
                            className={`grid grid-cols-[60px_1fr_160px_140px_160px_100px_60px] px-4 py-3 items-center border-b border-[#f0f4f8] hover:bg-[#fafcff] transition-colors ${
                                i % 2 === 0 ? 'bg-white' : 'bg-[#fafbfc]'
                            }`}
                        >
                            {/* ID */}
                            <span className='text-[12px] font-mono text-[#61748f]'>#{flow.id}</span>
                            {/* WABA ID + link to edit */}
                            <div className='flex items-center gap-2 min-w-0'>
                                <div className='w-8 h-8 rounded-xl bg-[#e6f9f1] flex items-center justify-center flex-shrink-0'>
                                    <FaWhatsapp className='text-[#37715d]' size={14} />
                                </div>
                                <div className='min-w-0'>
                                    <p className='text-[13px] font-bold text-[#1a2535] truncate'>
                                        {flow.waba_id || '—'}
                                    </p>
                                    <p className='text-[11px] text-[#61748f] truncate'>
                                        BIZ: {flow.business_id || '—'}
                                    </p>
                                </div>
                            </div>
                            {/* Type */}
                            <span className='text-[12px] text-[#576071] capitalize'>{flow.flow_type || 'build'}</span>
                            {/* Status */}
                            <StatusBadge status={flow.status} />
                            {/* Updated at */}
                            <span className='text-[11px] text-[#61748f]'>
                                {flow.updated_at
                                    ? new Date(flow.updated_at).toLocaleString('en-IN', {
                                        day: '2-digit', month: 'short', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit'
                                    })
                                    : '—'}
                            </span>
                            {/* Screen count */}
                            <span className='text-[12px] text-[#576071]'>
                                {flow.screen_count ?? '—'}
                            </span>
                            {/* Actions */}
                            <div className='flex items-center gap-1 justify-end'>
                                <button
                                    onClick={() => navigate(`/WAMessage/WAFlow/${flow.id}/edit`)}
                                    title='Edit'
                                    className='p-2 rounded-lg hover:bg-[#e6f9f1] text-[#37715d] transition-colors'
                                >
                                    <CiEdit size={16} />
                                </button>
                                <div className='relative'>
                                    <button
                                        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === flow.id ? null : flow.id); }}
                                        className='p-2 rounded-lg hover:bg-[#f0f4f8] text-[#61748f] transition-colors'
                                    >
                                        <BsThreeDotsVertical size={14} />
                                    </button>
                                    {openMenuId === flow.id && (
                                        <FlowMenu
                                            flow={flow}
                                            onStatusChange={handleStatusChange}
                                            onDelete={(id) => { setConfirmDelete(id); setOpenMenuId(null); }}
                                            onClose={() => setOpenMenuId(null)}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Footer count ─────────────────────────────────────────────── */}
            {!loading && filtered.length > 0 && (
                <p className='text-[11px] text-[#aab4c4] mt-3 text-right'>
                    Showing {filtered.length} of {flows.length} flow{flows.length !== 1 ? 's' : ''}
                </p>
            )}

            {/* ── Confirm delete dialog ─────────────────────────────────────── */}
            {confirmDelete && (
                <ConfirmDialog
                    message={`Delete flow #${confirmDelete}? This will remove all screens and cannot be undone.`}
                    onConfirm={() => handleDelete(confirmDelete)}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}

            {/* ── Toast ────────────────────────────────────────────────────── */}
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
}
